import { Box, Flex, Text } from "@chakra-ui/react";
import React, { useMemo } from "react";
import * as BoundaryService from "src/components/Boundary/services";
import { StatusTagMetadata } from "src/components/DataDisplay/StatusTag";
import { EligibilityServiceError } from "src/components/Feedback/EligibilityServiceError";
import {
  Loading,
  SchoolWithBoundaryTags,
} from "src/components/Inputs/MultiSelectSchoolRank";
import {
  addSchoolButtonState,
  schoolSelectionLimitText,
} from "src/components/Inputs/MultiSelectSchoolRank/helpers";
import { SchoolListItem } from "src/components/Inputs/MultiSelectSchoolRank/SchoolListItem";
import { RemoteDataView } from "src/components/Layout/RemoteDataView";
import { MultiSelectOrderableList } from "src/components/MultiSelectOrderableList";
import { Glossary } from "src/components/Text/Glossary";
import { useConfirmationDialog } from "src/hooks/useConfirmationDialog";
import { useGlossary } from "src/hooks/useGlossary";
import { useEligibleSchools } from "src/services/eligibility/useEligibleSchools";
import { findAddressQuestion } from "src/services/formTemplate/preRankingSection/determineEligibility";
import * as AF from "src/types/formTemplate";
import * as GQL from "src/types/graphql";
import * as RD from "src/types/remoteData";
import { LateEditSchoolsRankList } from "./LateEditSchoolsRankList";
import { LateEditRankedSchool, LateEditSchool } from "./types";

interface LateMultiSelectSchoolRankProps {
  formTemplateId: uuid;
  formId: uuid;
  enrollmentPeriodId: uuid;
  preRankingSection: AF.PreRankingSection<AF.WithId>;
  schoolRankingSection: AF.SchoolRankingSection<AF.WithId>;
  newSchools: readonly LateEditSchool[];
  selectedSchoolsRank: readonly LateEditRankedSchool[];
  onIsDropdownOpenChange?: (isOpen: boolean) => void;
  onLateDeleteSchoolRank: (schoolRank: LateEditRankedSchool) => void;
  onLateSelectedItem: (school: LateEditSchool) => void;
  onLateSchoolsUpdate: (schools: readonly LateEditRankedSchool[]) => void;
}

export const LateMultiSelectSchoolRank: React.FC<
  LateMultiSelectSchoolRankProps
> = ({
  formId,
  formTemplateId,
  enrollmentPeriodId,
  preRankingSection,
  schoolRankingSection,
  onIsDropdownOpenChange,
  newSchools,
  selectedSchoolsRank,
  onLateDeleteSchoolRank,
  onLateSelectedItem,
  onLateSchoolsUpdate,
}) => {
  const { glossary } = useGlossary();
  const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);

  const selectedSchools = React.useMemo(
    (): LateEditSchool[] => [
      ...selectedSchoolsRank?.map(
        (form_school_rank) => form_school_rank.school
      ),
      ...newSchools,
    ],
    [selectedSchoolsRank, newSchools]
  );

  const { remoteData: eligibleSchoolsRemoteData } = useEligibleSchools({
    formId,
    formTemplateId,
    enrollmentPeriodId,
    preRankingSection,
    selectedSchools,
  });

  const addressQuestionFilterType = findAddressQuestion(
    preRankingSection.questions
  )?.filter;

  const schoolLimitReached =
    schoolRankingSection.maxSchools === undefined
      ? false
      : selectedSchools.length >= schoolRankingSection.maxSchools;
  const renderListItem = (school: LateEditSchool) => (
    <SchoolListItem
      school={school}
      showAddress
      textProps={{ fontWeight: 600 }}
    />
  );

  const renderSelectItem = (
    school: LateEditSchool,
    subStatus?: StatusTagMetadata
  ) => (
    <SchoolListItem
      school={school}
      textProps={{ fontWeight: 600 }}
      subStatus={subStatus}
    />
  );
  const {
    confirm: confirmLateDeletion,
    confirmationDialog: lateDeleteConfirmationDialog,
    setBody,
  } = useConfirmationDialog({
    header: glossary`Remove school?`,
    body: null,
    cancelButton: {
      label: "No, cancel",
    },
    confirmButton: {
      label: "Yes, remove",
      colorScheme: "red",
    },
    translate: true,
  });

  const handleLateDeleteSchoolRank = async (
    schoolRank: LateEditRankedSchool
  ): Promise<boolean> => {
    if (
      schoolRank.sub_status === GQL.offer_status_enum.Accepted ||
      schoolRank.sub_status === GQL.offer_status_enum.Offered
    ) {
      setBody(
        <Text>
          This will remove it from your child’s form and decline the offer.
        </Text>
      );
    } else if (schoolRank.sub_status === GQL.waitlist_status_enum.Waitlisted) {
      setBody(
        <Text>
          This will remove it from your child’s form and withdraw from the
          waitlist.
        </Text>
      );
    } else {
      setBody(<Text>This will remove it from your child’s form.</Text>);
    }
    if (!(await confirmLateDeletion({}))) {
      return false;
    }

    onLateDeleteSchoolRank(schoolRank);
    return true;
  };

  const renderSelectedItems = (schools: readonly LateEditSchool[]) => {
    const schoolsRank: readonly LateEditRankedSchool[] =
      selectedSchoolsRank.map((sr) => {
        const school = schools.find((s) => s.id === sr.school.id);
        return {
          ...sr,
          school: {
            ...sr.school,
            boundaryTags: school?.boundaryTags,
          },
        };
      });

    return (
      <Flex
        direction="column"
        alignItems="center"
        maxWidth="25rem"
        width="100%"
        alignSelf="center"
      >
        {schools.length === 0 ? (
          <Box fontSize="xs" fontWeight="600">
            <Glossary>There are no schools selected</Glossary>
          </Box>
        ) : (
          <LateEditSchoolsRankList
            schoolsRank={schoolsRank}
            onDeleteSchoolRank={handleLateDeleteSchoolRank}
            renderSelectItem={renderSelectItem}
            rankingEnabled={schoolRankingSection.rankingEnabled ?? false}
            onSchoolRanksUpdate={onLateSchoolsUpdate}
          />
        )}
      </Flex>
    );
  };

  let schoolSelectionLimit: React.ReactNode = null;
  if (!isDropdownOpen) {
    const text = schoolSelectionLimitText(glossary, {
      limitReached: schoolLimitReached,
      maxSchools: schoolRankingSection.maxSchools,
      minSchools: schoolRankingSection.minSchools,
    });

    if (text) {
      schoolSelectionLimit = (
        <Text
          textAlign="center"
          alignSelf="center"
          color="gray.500"
          fontSize="sm"
        >
          {text}
        </Text>
      );
    }
  }

  const { withBoundaryTagsForList } =
    BoundaryService.useBoundaryTags<LateEditSchool>({
      formId,
    });
  const remoteData = useMemo(() => {
    return eligibleSchoolsRemoteData.andThen((eligibleSchools) => {
      // Add boundary tags to eligible schools list
      const eligibleSchoolsWithBoundaryTagsRD = withBoundaryTagsForList(
        eligibleSchools
      ).map(
        addressQuestionFilterType === "ShowFirst"
          ? BoundaryService.sortByShowFirst
          : BoundaryService.sortByFilter
      );

      // Add boundary tags to selected schools list
      const selectedSchoolsWithBoundaryTagsRD =
        withBoundaryTagsForList(selectedSchools);

      // Combine two remote data into tuple
      return RD.toTuple(
        eligibleSchoolsWithBoundaryTagsRD,
        selectedSchoolsWithBoundaryTagsRD
      );
    });
  }, [
    eligibleSchoolsRemoteData,
    selectedSchools,
    withBoundaryTagsForList,
    addressQuestionFilterType,
  ]);

  return (
    <Box width="100%" position="relative" display="block">
      <RemoteDataView
        error={(error) => <EligibilityServiceError error={error} />}
        remoteData={remoteData}
        loading={<Loading />}
      >
        {([
          eligibleSchoolsWithBoundaryTags,
          selectedSchoolsWithBoundaryTags,
        ]) => {
          return (
            <>
              <MultiSelectOrderableList<SchoolWithBoundaryTags>
                items={eligibleSchoolsWithBoundaryTags}
                selectedItems={selectedSchoolsWithBoundaryTags}
                keyAttr="id"
                searchableFields={["name", "street_address"]}
                label={glossary`Search schools to add`}
                hideLabel
                placeholder={glossary`Search by school name or address`}
                noResultsText={glossary`No schools found`}
                onSelectedItemChange={(school) => onLateSelectedItem(school)}
                renderListItem={renderListItem}
                renderSelectedItems={renderSelectedItems}
                button={{
                  label: glossary`Add school`,
                  ...addSchoolButtonState(glossary, {
                    limitReached: schoolLimitReached,
                    hasSchoolsToAdd: eligibleSchoolsWithBoundaryTags.length > 0,
                    hasSelectedSchools: selectedSchools.length > 0,
                  }),
                }}
                onIsDropdownOpenChange={(isOpen) => {
                  setIsDropdownOpen(isOpen);
                  if (onIsDropdownOpenChange) onIsDropdownOpenChange(isOpen);
                }}
              />

              {schoolSelectionLimit}
            </>
          );
        }}
      </RemoteDataView>
      {lateDeleteConfirmationDialog}
    </Box>
  );
};
